import DataTable from "react-data-table-component";
import { useEffect, useState } from "react";
import Button from "../../../../components/common/button";
import Switch from "../../../../components/common/Switch";
import Select from "../../../../components/common/Select";
import { ON_OFF_OPTIONS } from "../../../../utils/constants";
import AddWhiteNoiseModal from "../../../../components/tv-app/sleep-concierge/add-white-noise-modal";
import DeleteAndroidHome from "../../../../components/tv-app/delete-android-home";
import EditTableRowName from "../../../../components/tv-app/edit-name";
import DiscardSaveButtons from "../../../../components/tv-app/discard-save-buttons";
import {
  IMusicTime,
  ISleepCategoryComponentProps,
  ITableColumn,
  IWhiteNoiseSubData,
  SleepConciergeSubDataTypes,
} from "../../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import useUpdateAndroidTVHomeOrder from "../../../../hooks/use-update-android-home-order";
import DraggableRow from "../../../../components/common/DragRow";
import { DragSvg } from "../../../../icons";
import {
  convertTimeToAmPmFormat,
  convertToTimeInputFormat,
  setDragOnlyInStaging,
} from "../../../../utils/helpers";
import TimeInput from "../../../../components/common/TimeInput";
import { useGetUserPermission } from "../../../../context/AuthStaffUserContext";

// type OmitIMusicTime = Omit<IMusicTime, "time"> & {
//   time: { label: string; value: string } | undefined;
// };

type IFormData = {
  showWN: boolean;
  defaultOnOrOff?: { label: string; value: "on" | "off" };
  data: IWhiteNoiseSubData[];
  times: {
    startTime: string;
    endTime: string;
  };
};

const WhiteNoise = ({
  subData,
  time,
  show,
  defaultOnOff,
  onSave,
  loading,
  categoryName,
  type,
}: ISleepCategoryComponentProps<IWhiteNoiseSubData[]>) => {
  const getInitialFormData = () => {
    const start = time?.find((t) => t.name === "Start Time")?.time || "";
    const end = time?.find((t) => t.name === "End Time")?.time || "";

    return {
      showWN: !!show,
      defaultOnOrOff: ON_OFF_OPTIONS.find(
        (item) => item.value === (defaultOnOff || "off")
      ),
      data: subData.sort((a, b) => a?.orderKey - b?.orderKey) ?? [],
      times: {
        startTime: convertToTimeInputFormat(start),
        endTime: convertToTimeInputFormat(end),
      },
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });
  const { reset, handleSubmit, setValue, watch } = methods;
  const { times } = watch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const {
    fields: sleepCatagoryWNFields,
    append: appendSleepCatagoryWNField,
    remove: removeSleepCatagoryWNField,
  } = useFieldArray({
    name: "data",
    control: methods.control,
    keyName: "customId",
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const [whiteNoiseData, setWhiteNoiseData] = useState(() => ({
    categoryName,
    items:
      [...(sleepCatagoryWNFields as IWhiteNoiseSubData[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    if (sleepCatagoryWNFields.length < subData.length) {
      const addedElement = subData[subData.length - 1];
      appendSleepCatagoryWNField({
        ...addedElement,
        isSelected: addedElement.defaultPlaying,
      });

      setInitialFormData((pre) => {
        const prevData = pre.data
          .sort((a, b) => a?.orderKey - b?.orderKey)
          .map((ele, index) => {
            if (addedElement.defaultPlaying) {
              methods.setValue(`data.${index}.isSelected`, false);
            }
            return {
              ...ele,
              isSelected: addedElement.defaultPlaying ? false : ele.isSelected,
            };
          });
        return {
          ...pre,
          data: [...prevData, { ...addedElement }],
        };
      });
      methods.trigger();
    }
  }, [subData]);

  useEffect(() => {
    setWhiteNoiseData((prev) => {
      const existingIds = new Set(prev.items.map((item) => item.id));
      const newItems = sleepCatagoryWNFields.filter(
        (item) => item.id && !existingIds.has(item.id)
      );
      return {
        ...prev,
        items: [...prev.items, ...newItems].sort(
          (a, b) => a.orderKey - b.orderKey
        ),
      };
    });
  }, [sleepCatagoryWNFields]);

  const getTableDataIndex = (id: string) => {
    return sleepCatagoryWNFields.findIndex((item) => item.id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...whiteNoiseData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));
    setWhiteNoiseData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));

    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));
    updateAndroidTVHomeOrder({
      categoryName,
      type,
      data: updatedDataWithOrderKey,
    });
  };

  const onSubmit = (submitData: IFormData) => {
    const { startTime, endTime } = submitData.times;

    const times: Array<IMusicTime> = [
      {
        name: "Start Time",
        time: convertTimeToAmPmFormat(startTime),
        type: SleepConciergeSubDataTypes.SELECT_TIME,
      },
      {
        name: "End Time",
        time: convertTimeToAmPmFormat(endTime),
        type: SleepConciergeSubDataTypes.SELECT_TIME,
      },
    ];

    const orderKeyMap = new Map(
      whiteNoiseData.items.map((item) => [item.id, item.orderKey])
    );

    const mergedData = submitData.data
      .filter((item) => item?.id?.trim())
      .map((item) => ({
        ...item,
        orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
      }));

    reset((prev) => ({
      ...prev,
      data: mergedData,
    }));

    onSave({
      show: submitData.showWN,
      subData: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
      time: times,
      defaultOnOff: submitData.defaultOnOrOff?.value,
    });
  };

  const handleSwitchChange = (rowId: string) => {
    const updatedItems = whiteNoiseData.items
      .sort((a, b) => a?.orderKey - b?.orderKey)
      .map((item, index) => {
        const isSelected = item.id === rowId;

        methods.setValue(`data.${index}.isSelected`, isSelected);

        return {
          ...item,
          isSelected,
        };
      });

    methods.trigger();

    setWhiteNoiseData({ ...whiteNoiseData, items: updatedItems });
  };

  const tableColumns: Array<ITableColumn<IWhiteNoiseSubData>> =
    setDragOnlyInStaging<ITableColumn<IWhiteNoiseSubData>>([
      {
        name: "",
        cell: (Row, index) => (
          <DraggableRow
            index={index}
            moveRow={moveRow}
            onDropComplete={onDropComplete}
          >
            <DragSvg
              height={24}
              width={24}
              className="h-6 w-6 cursor-grab flex-none"
            />
          </DraggableRow>
        ),
        sortable: true,
        width: "10%",
      },
      {
        name: "Name",
        cell: (Row) => {
          const index = getTableDataIndex(Row.id);
          if (index === -1) return null;
          return (
            <EditTableRowName
              name={`data.${index}.name`}
              disabled={!isEditPermission}
            />
          );
        },
        sortable: true,
      },
      {
        name: "Default",
        cell: (Row) => (
          <Switch
            name={`data.${getTableDataIndex(Row.id)}.isSelected`}
            onCheck={() => handleSwitchChange(Row.id)}
          />
        ),
        sortable: true,
        center: true,
      },
      {
        name: "Hide/Show",
        cell: (Row) => (
          <Switch name={`data.${getTableDataIndex(Row.id)}.selected`} />
        ),
        sortable: true,
        center: true,
      },
      {
        name: "Action",
        cell: (Row) => (
          <DeleteAndroidHome
            categoryName={categoryName}
            type={type}
            deleteId={Row.id}
            disabled={!isEditPermission}
            removeField={() => {
              setInitialFormData((pre) => ({
                ...pre,
                data: pre.data.filter((item) => item.id !== Row.id),
              }));
              removeSleepCatagoryWNField(getTableDataIndex(Row.id));
              setWhiteNoiseData({
                ...whiteNoiseData,
                items: whiteNoiseData.items.filter(
                  (item) => item.id !== Row.id
                ),
              });
            }}
          />
        ),
        sortable: true,
        center: true,
      },
    ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!whiteNoiseData?.items) return;
    const globalFromIndex = (currentPage - 1) * pageSize + fromIndex;
    const globalToIndex = (currentPage - 1) * pageSize + toIndex;

    const updatedData = [...whiteNoiseData.items];
    const [movedItem] = updatedData.splice(globalFromIndex, 1);
    updatedData.splice(globalToIndex, 0, movedItem);

    const reOrdered = updatedData.map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setWhiteNoiseData({
      ...whiteNoiseData,
      items: reOrdered,
    });
  };
  useEffect(() => {
    methods.trigger();
  }, [subData]);

  const { write: isEditPermission } = useGetUserPermission("tv-app");

  return (
    <FormProvider {...methods}>
      <div className="w-full mt-4">
        <div className="">
          {/* <Switch
            name="showWN"
            label="White Noise for Better Sleep  (Hide/Show)"
          /> */}
        </div>
        <h4 className="text-base leading-[39px] font-normal text-[#1E2F65] whitespace-nowrap">
          White Noise for Better Sleep
        </h4>
        <div className="grid grid-cols-3 gap-2 relative">
          <Select
            name="defaultOnOrOff"
            label="Default ON/OFF"
            options={ON_OFF_OPTIONS}
          />
          <TimeInput
            name="times.startTime"
            rules={{ required: "Start Time is required" }}
            type="time"
            label="Default Start Time"
            placeholder="03: 00 PM"
            value={times.startTime || ""}
            onChange={(e) => setValue("times.startTime", e.target.value)}
          />
          <TimeInput
            name="times.endTime"
            rules={{ required: "Start Time is required" }}
            type="time"
            label="Default End Time"
            placeholder="03: 00 PM"
            value={times.endTime || ""}
            onChange={(e) => setValue("times.endTime", e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between w-full mt-7.5">
          <h4 className="text-base leading-[39px] font-normal text-[#1E2F65]">
            Music List
          </h4>
          <Button
            variant="primary"
            className="w-21"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add
          </Button>
        </div>

        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={whiteNoiseData.items}
            columns={tableColumns}
            striped={true}
            keyField="customId"
            persistTableHead
            pagination={sleepCatagoryWNFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(perPage) => setPageSize(perPage)}
          />
        </DndProvider>

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData());
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddWhiteNoiseModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={categoryName}
          type={type}
        />
      </div>
    </FormProvider>
  );
};

export default WhiteNoise;
